<template>
  <div class="admin-settings">
    <!-- Navbar already shows pageTitle — keep a short intro only -->
    <div class="admin-settings__page-header mb-1">
      <div class="d-flex flex-wrap align-items-start justify-content-between">
        <p class="text-muted admin-settings__page-desc mb-0">
          Pick a section. Create agents first, then turn on AI Calling.
        </p>
        <b-badge v-if="canAccessPage" variant="light-primary" class="admin-settings__role-badge mt-25 mt-md-0">
          <feather-icon icon="ShieldIcon" size="14" class="mr-25" />
          Settings access
        </b-badge>
      </div>
    </div>

    <b-alert v-if="!canAccessPage" variant="warning" show class="mb-0">
      <div class="d-flex align-items-start">
        <feather-icon icon="AlertTriangleIcon" size="18" class="mr-75 flex-shrink-0 mt-25" />
        <div>
          <strong class="d-block mb-25">Access restricted</strong>
          <span class="small">You do not have permission to manage any admin settings. Contact your administrator if you need access.</span>
        </div>
      </div>
    </b-alert>

    <b-row v-else class="admin-settings__layout" no-gutters>
      <!-- Sidebar navigation -->
      <b-col cols="12" lg="3" xl="2" class="admin-settings__nav-col pr-lg-1">
        <b-card body-class="p-75 admin-settings__nav-card" class="admin-settings__nav-wrapper mb-1 mb-lg-0">
          <nav class="admin-settings__nav" aria-label="Admin settings sections">
            <button
              v-for="tab in visibleTabs"
              :key="tab.key"
              type="button"
              class="admin-settings__nav-item"
              :class="{ 'admin-settings__nav-item--active': activeTabKey === tab.key }"
              :aria-current="activeTabKey === tab.key ? 'page' : null"
              @click="selectTab(tab.key)"
            >
              <span class="admin-settings__nav-icon">
                <feather-icon :icon="tab.icon" size="16" />
              </span>
              <span class="admin-settings__nav-label">
                <span class="admin-settings__nav-title">{{ tab.title }}</span>
                <span class="admin-settings__nav-desc">{{ tab.description }}</span>
              </span>
              <feather-icon
                icon="ChevronRightIcon"
                size="14"
                class="admin-settings__nav-chevron d-none d-lg-block"
              />
            </button>
          </nav>
        </b-card>
      </b-col>

      <!-- Main content -->
      <b-col cols="12" lg="9" xl="10" class="admin-settings__content-col pl-lg-1">
        <b-card body-class="p-1 p-md-2 admin-settings__content-card">
          <admin-section-header
            v-if="activeTabKey !== 'agents' && activeTabKey !== 'ai-calling'"
            :title="activeTabMeta.title"
            :description="activeTabMeta.description"
            :icon="activeTabMeta.icon"
          />

          <div
            class="admin-settings__panel"
            :class="{
              'mt-1': activeTabKey !== 'agents' && activeTabKey !== 'ai-calling',
              'admin-settings__panel--flush': activeTabKey === 'agents' || activeTabKey === 'ai-calling',
            }"
            :key="activeTabKey"
          >
            <admin-master-data-panel
              v-if="activeTabKey === 'master-data' && canManageMasterDataTab"
              embedded
            />
            <admin-agents-panel
              v-else-if="activeTabKey === 'agents' && canManageAgentsTab"
              embedded
            />
            <ai-calling-settings
              v-else-if="activeTabKey === 'ai-calling' && canManageAiCallingTab"
              embedded
            />
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BAlert,
  BRow,
  BCol,
  BBadge,
} from 'bootstrap-vue'
import AiCallingSettings from '@/views/apps/calls/AiCallingSettings.vue'
import AdminAgentsPanel from '@/views/apps/admin/AdminAgentsPanel.vue'
import AdminMasterDataPanel from '@/views/apps/admin/AdminMasterDataPanel.vue'
import AdminSectionHeader from '@/views/apps/admin/components/AdminSectionHeader.vue'
import {
  canManageAgentsTab as userCanManageAgentsTab,
  canManageAiCallingTab as userCanManageAiCallingTab,
  canManageMasterData,
} from '@/utils/admin-settings-permissions'

const TAB_CATALOG = {
  'master-data': {
    key: 'master-data',
    title: 'Master data',
    description: 'DID Assignment, clients, locations, and job dropdown values.',
    icon: 'DatabaseIcon',
  },
  agents: {
    key: 'agents',
    title: 'Agents',
    description: 'Voice + script that talks to candidates.',
    icon: 'UsersIcon',
  },
  'ai-calling': {
    key: 'ai-calling',
    title: 'AI Calling',
    description: 'Phones candidates see and who handles each call type.',
    icon: 'PhoneCallIcon',
  },
}

export default {
  name: 'AdminSettings',
  components: {
    BCard,
    BAlert,
    BRow,
    BCol,
    BBadge,
    AiCallingSettings,
    AdminAgentsPanel,
    AdminMasterDataPanel,
    AdminSectionHeader,
  },
  data() {
    return {
      activeTabKey: '',
    }
  },
  computed: {
    canManageMasterDataTab() {
      return canManageMasterData(this.$can.bind(this))
    },
    canManageAgentsTab() {
      return userCanManageAgentsTab(this.$can.bind(this))
    },
    canManageAiCallingTab() {
      return userCanManageAiCallingTab(this.$can.bind(this))
    },
    visibleTabs() {
      const tabs = []
      if (this.canManageMasterDataTab) {
        tabs.push(TAB_CATALOG['master-data'])
      }
      if (this.canManageAgentsTab) {
        tabs.push(TAB_CATALOG.agents)
      }
      if (this.canManageAiCallingTab) {
        tabs.push(TAB_CATALOG['ai-calling'])
      }
      return tabs
    },
    canAccessPage() {
      return this.visibleTabs.length > 0
    },
    activeTabMeta() {
      return TAB_CATALOG[this.activeTabKey] || this.visibleTabs[0] || TAB_CATALOG['master-data']
    },
  },
  watch: {
    '$route.query.tab'() {
      this.syncTabFromRoute()
    },
    visibleTabs() {
      this.syncTabFromRoute()
    },
  },
  created() {
    this.syncTabFromRoute()
  },
  methods: {
    syncTabFromRoute() {
      if (!this.canAccessPage) {
        this.activeTabKey = ''
        return
      }
      const q = this.$route.query && this.$route.query.tab
      const match = this.visibleTabs.find(t => t.key === q)
      const nextKey = match ? match.key : (this.visibleTabs[0] && this.visibleTabs[0].key) || ''
      this.activeTabKey = nextKey
      if (nextKey && q !== nextKey) {
        const query = { ...this.$route.query, tab: nextKey }
        if (nextKey !== 'master-data') {
          delete query.section
        }
        this.$router.replace({
          name: 'apps-admin-settings',
          query,
        }).catch(() => {})
      }
    },
    selectTab(key) {
      if (!key || !this.visibleTabs.some(t => t.key === key)) return
      if (this.activeTabKey === key) return
      this.activeTabKey = key
      const query = { ...this.$route.query, tab: key }
      if (key !== 'master-data') {
        delete query.section
      }
      const currentQuery = this.$route.query || {}
      const unchanged = currentQuery.tab === key
        && (key !== 'master-data' || currentQuery.section === query.section)
      if (unchanged) return
      this.$router.replace({
        name: 'apps-admin-settings',
        query,
      }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.admin-settings {
  margin-top: -0.85rem;
}
.admin-settings__page-desc {
  font-size: 0.875rem;
  max-width: 36rem;
  line-height: 1.45;
}
.admin-settings__role-badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.35rem 0.65rem;
}
.admin-settings__nav-col {
  min-width: 0;
}
.admin-settings__nav-wrapper {
  position: sticky;
  top: 5rem;
}
.admin-settings__nav {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
@media (max-width: 991.98px) {
  .admin-settings__nav {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
}
.admin-settings__nav-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.65rem 0.75rem;
  border: 1px solid transparent;
  border-radius: 0.428rem;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}
@media (max-width: 991.98px) {
  .admin-settings__nav-item {
    width: auto;
    flex: 1 1 calc(50% - 0.25rem);
    min-width: 9rem;
  }
}
.admin-settings__nav-item:hover {
  background: rgba(115, 103, 240, 0.06);
}
.admin-settings__nav-item:focus-visible {
  outline: 2px solid #7367f0;
  outline-offset: 2px;
}
.admin-settings__nav-item--active {
  background: rgba(115, 103, 240, 0.1);
  border-color: rgba(115, 103, 240, 0.35);
  box-shadow: 0 2px 4px rgba(115, 103, 240, 0.08);
}
.admin-settings__nav-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  margin-right: 0.65rem;
  border-radius: 0.357rem;
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
  flex-shrink: 0;
}
.admin-settings__nav-item--active .admin-settings__nav-icon {
  background: #7367f0;
  color: #fff;
}
.admin-settings__nav-label {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.admin-settings__nav-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #5e5873;
  line-height: 1.3;
}
.admin-settings__nav-item--active .admin-settings__nav-title {
  color: #7367f0;
}
.admin-settings__nav-desc {
  display: none;
  font-size: 0.75rem;
  color: #b9b9c3;
  line-height: 1.35;
  margin-top: 0.15rem;
}
@media (min-width: 992px) {
  .admin-settings__nav-desc {
    display: block;
  }
}
.admin-settings__nav-chevron {
  margin-left: 0.35rem;
  color: #b9b9c3;
  flex-shrink: 0;
}
.admin-settings__nav-item--active .admin-settings__nav-chevron {
  color: #7367f0;
}
.admin-settings__content-card {
  min-height: 20rem;
}
.admin-settings__panel {
  border-top: 1px solid #ebe9f1;
  padding-top: 1rem;
}
.admin-settings__panel--flush {
  border-top: 0;
  padding-top: 0;
}
</style>
